import type { BankHoliday, Booking, Resource, WorkingHoursProfile } from '@/types';
import { isAssetResourceType } from '@/lib/resource-types';

export const UNALLOCATED_RESOURCE_ID = 'unallocated';
export const UNALLOCATED_RESOURCE_GROUP_NAME = 'Unallocated';

export const UNALLOCATED_RESOURCE: Resource = {
  id: UNALLOCATED_RESOURCE_ID,
  name: 'Unallocated',
  role: '',
  status: 'available',
  type: 'permanent',
  photoUrl: undefined,
  department: 'System',
  schedulerGroup: 'System',
};

export function withUnallocatedResource(resources: Resource[]): Resource[] {
  if (resources.some((resource) => String(resource.id) === UNALLOCATED_RESOURCE_ID)) {
    return resources;
  }

  return [...resources, UNALLOCATED_RESOURCE];
}

export function getUnallocatedAwareGroupName(
  resource: Pick<Resource, 'id'>,
  fallbackGroupName: string,
): string {
  return String(resource.id) === UNALLOCATED_RESOURCE_ID
    ? UNALLOCATED_RESOURCE_GROUP_NAME
    : fallbackGroupName;
}

export function getResourcePickerOptions(resources: Resource[], isLive: boolean): Resource[] {
  if (!isLive || resources.some((resource) => String(resource.id) === UNALLOCATED_RESOURCE_ID)) {
    return resources;
  }

  return [UNALLOCATED_RESOURCE, ...resources];
}

function toLocalDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/**
 * The days in the range that are bank holidays, keyed the same way as the days
 * walked below so a holiday counts as non-working for every resource.
 */
function bankHolidayDayKeys(bankHolidays: BankHoliday[]): Set<string> {
  const keys = new Set<string>();

  for (const holiday of bankHolidays) {
    // A date-only value is read as a local day, not the UTC instant Date() would give it.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(holiday.date);
    if (dateOnly) {
      keys.add(`${Number(dateOnly[1])}-${Number(dateOnly[2])}-${Number(dateOnly[3])}`);
      continue;
    }

    const date = new Date(holiday.date);
    if (Number.isNaN(date.getTime())) continue;
    keys.add(toLocalDayKey(date));
  }

  return keys;
}

export function filterResourcesByWorkingDays(
  resources: Resource[],
  startDate: Date | undefined,
  endDate: Date | undefined,
  globalProfile: WorkingHoursProfile | undefined,
  selectedResourceIds: string[],
  bankHolidays: BankHoliday[] = [],
): Resource[] {
  if (!startDate || !endDate || !globalProfile) return resources;

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
  const holidayKeys = bankHolidayDayKeys(bankHolidays);

  return resources.filter((resource) => {
    if (String(resource.id) === UNALLOCATED_RESOURCE_ID) return true;
    if (selectedResourceIds.includes(String(resource.id))) return true;
    // Assets keep no working pattern, so a working-day rule cannot rule them out.
    if (isAssetResourceType(resource.type)) return true;

    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    while (current <= end) {
      if (holidayKeys.has(toLocalDayKey(current))) return false;
      const dayKey = dayNames[current.getDay()];
      const profile = resource.useCustomWorkingProfile && resource.customWorkingProfile
        ? resource.customWorkingProfile
        : globalProfile;
      if ((Number(profile[dayKey]) || 0) === 0) return false;
      current.setDate(current.getDate() + 1);
    }

    return true;
  });
}

export function normalizeResourceSelection(nextIds: string[], previousIds: string[]): string[] {
  if (!nextIds.includes(UNALLOCATED_RESOURCE_ID)) return nextIds;
  if (!previousIds.includes(UNALLOCATED_RESOURCE_ID)) return [UNALLOCATED_RESOURCE_ID];

  const allocatedIds = nextIds.filter((id) => id !== UNALLOCATED_RESOURCE_ID);
  return allocatedIds.length > 0 ? allocatedIds : [UNALLOCATED_RESOURCE_ID];
}

export function hasResourceSelection(resourceIds: string[]): boolean {
  return resourceIds.length > 0;
}

export function isUnallocatedSelection(resourceIds: string[]): boolean {
  return resourceIds.includes(UNALLOCATED_RESOURCE_ID);
}

/**
 * A booking nobody is assigned to yet. Live data records that as no resource at
 * all and demo data as the synthetic Unallocated resource, so both conventions
 * count.
 */
export function isUnallocatedBooking(booking: Pick<Booking, 'resourceId'>): boolean {
  return booking.resourceId == null
    || String(booking.resourceId) === UNALLOCATED_RESOURCE_ID;
}

/**
 * The wording shown wherever a repeat is refused for the Unallocated resource. The
 * quoted phrase is styled apart from the rest of the sentence where the surface
 * allows it.
 */
export const UNALLOCATED_REPEAT_ERROR_SUBJECT = 'Unallocated Resource';
export const UNALLOCATED_REPEAT_ERROR_PREFIX = 'Repeat bookings cannot be created for the';
export const UNALLOCATED_REPEAT_ERROR = `${UNALLOCATED_REPEAT_ERROR_PREFIX} "${UNALLOCATED_REPEAT_ERROR_SUBJECT}".`;

/**
 * A booking that repeats. An occurrence of a series carries the group rather than
 * the pattern itself, so either marks the booking as repeating.
 */
export function isRepeatBooking(booking: Pick<Booking, 'recurrence' | 'recurrenceGroupId'>): boolean {
  return !!booking.recurrenceGroupId
    || (!!booking.recurrence && booking.recurrence.pattern !== 'none');
}

/**
 * Saves the Unallocated selection in the convention of the mode it was made in —
 * no resource at all for live data, the synthetic Unallocated resource for demo
 * data — so reopening the booking restores the same selection either way.
 */
export function getBookingResourceFields(
  resourceIds: string[],
  isLive: boolean,
): Pick<Booking, 'resourceId' | 'resourceIds'> {
  if (isUnallocatedSelection(resourceIds)) {
    return { resourceId: isLive ? null : UNALLOCATED_RESOURCE_ID, resourceIds: undefined };
  }

  return {
    resourceId: resourceIds[0] ?? null,
    resourceIds: resourceIds.length > 0 ? resourceIds : undefined,
  };
}
